import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, List, RefreshCw, Search, X, Eye, Edit2, ChevronUp, ChevronDown } from 'lucide-react';
import { apiClient } from '@rti/shared';
import { bulletsAPI } from '@/services/commentary.service';
import type { EmailBullet, CreateBulletPayload, UpdateBulletPayload } from '@/types/emailBullets';
import RichTextEditor from '@/components/RichTextEditor';
import ConfirmationModal from '@/components/ConfirmationModal';

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateDisplay(iso: string) {
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatUpdated(iso: string) {
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
      timeZone: 'America/Chicago',
    });
  } catch {
    return iso;
  }
}

// ── Form (add / edit) ────────────────────────────────────────────────────────

interface BulletFormProps {
  initialData: EmailBullet | null;
  onSave: () => void;
  onCancel: () => void;
}

const BulletForm: React.FC<BulletFormProps> = ({ initialData, onSave, onCancel }) => {
  const [date, setDate] = useState(initialData?.date ?? todayIso());
  const [title, setTitle] = useState(initialData?.title ?? '');
  const [bullets, setBullets] = useState(initialData?.bullets ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = initialData != null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!date || !bullets.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = { date, title: title.trim() || undefined, bullets: bullets.trim() };
      if (isEdit) {
        await bulletsAPI.staging.update(initialData!.id, payload as UpdateBulletPayload);
      } else {
        await bulletsAPI.staging.create(payload as CreateBulletPayload);
      }
      onSave();
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div className="flex gap-4 items-start">
        <div className="flex-shrink-0">
          <label className="block text-xs text-rs-off-white/50 mb-1">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
            style={{ colorScheme: 'dark' }}
            className="bg-rs-off-black border border-rs-off-white/20 text-rs-off-white text-sm rounded px-3 py-2 focus:outline-none focus:border-rs-green"
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs text-rs-off-white/50 mb-1">
            Title <span className="text-rs-off-white/30">(optional)</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Tariff Impact on Resin Pricing"
            className="bg-rs-off-black border border-rs-off-white/20 text-rs-off-white text-sm rounded px-3 py-2 focus:outline-none focus:border-rs-green w-full"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs text-rs-off-white/50 mb-1">Bullets</label>
        <RichTextEditor
          value={bullets}
          onChange={setBullets}
          placeholder="Enter market bullet points..."
          rows={8}
        />
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <div className="flex justify-end gap-3 pt-2 border-t border-rs-jet-light">
        <button
          type="button"
          onClick={onCancel}
          className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-4 py-2 rounded transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting || !date || !bullets.trim()}
          className="bg-rs-green hover:bg-rs-green/80 disabled:opacity-50 !text-rs-off-black font-semibold px-5 py-2 rounded transition-colors"
        >
          {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Add to Staging'}
        </button>
      </div>
    </form>
  );
};

// ── Pending Preview Modal ─────────────────────────────────────────────────────

interface PendingPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  rows: EmailBullet[];
}

const PendingPreviewModal: React.FC<PendingPreviewModalProps> = ({ isOpen, onClose, rows }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-rs-jet border border-rs-jet-light rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-rs-jet-light">
          <h2 className="text-lg font-semibold text-rs-green">
            Pending Staging Bullets ({rows.length})
          </h2>
          <button onClick={onClose} className="text-rs-off-white/50 hover:text-rs-off-white transition-colors">
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-rs-jet-light">
          {rows.map((row) => (
            <div key={row.id} className="px-6 py-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-xs text-rs-off-white/60">{formatDateDisplay(row.date)}</span>
                {row.title && (
                  <span className="text-sm font-semibold text-rs-off-white">{row.title}</span>
                )}
              </div>
              <div
                className="text-sm text-rs-off-white/70 leading-relaxed prose prose-invert prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: row.bullets }}
              />
              {row.last_updated && (
                <p className="text-xs text-rs-off-white/30 mt-2">{formatUpdated(row.last_updated)}</p>
              )}
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-rs-jet-light flex justify-end">
          <button
            onClick={onClose}
            className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-4 py-2 rounded transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Panel ───────────────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc';

const EmailBulletsPanel: React.FC = () => {
  const [viewMode, setViewMode] = useState<'list' | 'form'>('list');
  const [editingRecord, setEditingRecord] = useState<EmailBullet | null>(null);
  const [published, setPublished] = useState<EmailBullet[]>([]);
  const [staging, setStaging] = useState<EmailBullet[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishSubmitting, setPublishSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteSource, setDeleteSource] = useState<'staging' | 'published'>('staging');
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pub, stg] = await Promise.all([bulletsAPI.getAll(), bulletsAPI.staging.getAll()]);
      setPublished(pub);
      setStaging(stg);
    } catch {
      showToast('Failed to load bullets', false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleEdit = (row: EmailBullet) => {
    setEditingRecord(row);
    setViewMode('form');
  };

  const handleNew = () => {
    setEditingRecord(null);
    setViewMode('form');
  };

  const handleBackToList = () => {
    setEditingRecord(null);
    setViewMode('list');
    load();
  };

  const handleFormSave = () => {
    showToast(editingRecord ? 'Bullet saved to staging' : 'Bullet added to staging');
    handleBackToList();
  };

  const handlePublish = async () => {
    setPublishSubmitting(true);
    try {
      const result = await bulletsAPI.staging.publish();
      showToast(`Published ${result.published} bullet${result.published !== 1 ? 's' : ''} to live feed`);
      await Promise.all([load(), apiClient.fetchEmailBulletsData().catch(() => {})]);
    } catch {
      showToast('Failed to publish', false);
    } finally {
      setPublishSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteId == null) return;
    try {
      if (deleteSource === 'staging') {
        await bulletsAPI.staging.delete(deleteId);
      } else {
        await bulletsAPI.delete(deleteId);
      }
      showToast('Bullet deleted');
      setDeleteId(null);
      await load();
    } catch {
      showToast('Failed to delete', false);
      setDeleteId(null);
    }
  };

  const allRows: (EmailBullet & { _source: 'staging' | 'published' })[] = [
    ...staging.map((r) => ({ ...r, _source: 'staging' as const })),
    ...published.map((r) => ({ ...r, _source: 'published' as const })),
  ];

  const filtered = allRows.filter((row) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      row.date.includes(q) ||
      (row.title ?? '').toLowerCase().includes(q) ||
      row.bullets.replace(/<[^>]*>/g, '').toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const cmp = a.date.localeCompare(b.date);
    return sortDir === 'desc' ? -cmp : cmp;
  });

  return (
    <>
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 rounded px-4 py-3 text-sm font-medium shadow-lg ${toast.ok ? 'bg-rs-green text-rs-off-black' : 'bg-red-600 text-white'}`}>
          {toast.msg}
        </div>
      )}

      {/* Section header */}
      <div className="mb-4 pb-4 border-b border-rs-jet-light flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-rs-green">
            Email Bullets
            {viewMode === 'form' && (
              <span className="text-sm text-rs-off-white/70 ml-3">
                {editingRecord ? '(Editing)' : '(New Record)'}
              </span>
            )}
          </h2>
          <p className="text-sm text-rs-off-white/70 mt-1">
            Market bullet summaries sent via RSS email feed
          </p>
        </div>

        {viewMode === 'list' ? (
          <div className="flex gap-2 items-center">
            <button
              onClick={load}
              className="text-rs-off-white/50 hover:text-rs-green p-1.5 rounded transition-colors"
              title="Refresh"
            >
              <RefreshCw size={15} />
            </button>
            {staging.length > 0 && (
              <>
                <button
                  onClick={() => setShowPendingModal(true)}
                  className="bg-rs-salmon hover:bg-rs-salmon/80 !text-rs-off-black font-semibold text-sm px-4 py-2 rounded transition-colors flex items-center gap-2"
                >
                  <Eye size={16} />
                  Preview Pending ({staging.length})
                </button>
                <button
                  onClick={handlePublish}
                  disabled={publishSubmitting}
                  className="bg-rs-green hover:bg-rs-light-green disabled:opacity-50 !text-rs-off-black font-semibold text-sm px-4 py-2 rounded transition-colors flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {publishSubmitting ? 'Publishing...' : 'Publish Changes'}
                </button>
              </>
            )}
            <button
              onClick={handleNew}
              className="flex items-center gap-2 bg-rs-green hover:bg-rs-light-green !text-rs-off-black font-semibold px-4 py-2 rounded transition-colors"
            >
              <PlusCircle size={16} />
              New Record
            </button>
          </div>
        ) : (
          <button
            onClick={handleBackToList}
            className="flex items-center gap-2 bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white font-semibold px-4 py-2 rounded transition-colors"
          >
            <List size={16} />
            Back to List
          </button>
        )}
      </div>

      {/* Form view */}
      {viewMode === 'form' && (
        <BulletForm
          initialData={editingRecord}
          onSave={handleFormSave}
          onCancel={handleBackToList}
        />
      )}

      {/* List view */}
      {viewMode === 'list' && (
        <>
          {/* Search bar */}
          <div className="mb-4 flex items-center gap-4">
            <div className="flex-1 relative">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-rs-off-white/50" />
              <input
                type="text"
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-rs-jet border border-rs-jet-light rounded pl-10 pr-4 py-2 text-rs-off-white placeholder-rs-off-white/50 focus:border-rs-green focus:outline-none"
              />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="bg-rs-jet-light hover:bg-rs-jet-light/70 text-rs-off-white px-4 py-2 rounded transition-colors flex items-center gap-2"
              >
                <X size={16} />
                Clear
              </button>
            )}
          </div>

          <div className="mb-3 text-sm text-rs-off-white/70">
            {sorted.length} of {allRows.length} records
            {searchTerm && ` matching "${searchTerm}"`}
          </div>

          {loading ? (
            <p className="text-rs-off-white/50 text-sm py-8 text-center">Loading...</p>
          ) : sorted.length === 0 ? (
            <div className="text-center py-12 text-rs-off-white/50">
              {allRows.length === 0 ? (
                <>
                  <p>No bullets yet.</p>
                  <p className="text-xs mt-2">Click "New Record" to add one.</p>
                </>
              ) : (
                <>
                  <Search size={48} className="mx-auto mb-4 opacity-30" />
                  <p>No records match your search</p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto border border-rs-jet-light rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-rs-jet border-b border-rs-jet-light">
                  <tr className="text-rs-green text-xs uppercase tracking-wide">
                    <th
                      className="px-4 py-3 text-left w-32 cursor-pointer hover:bg-rs-jet-light select-none"
                      onClick={() => setSortDir((d) => d === 'desc' ? 'asc' : 'desc')}
                    >
                      <span className="inline-flex items-center gap-1">
                        Date
                        {sortDir === 'desc' ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                      </span>
                    </th>
                    <th className="px-4 py-3 text-left w-52">Title</th>
                    <th className="px-4 py-3 text-left">Bullets</th>
                    <th className="px-4 py-3 text-left w-44">Last Updated</th>
                    <th className="px-4 py-3 text-right w-28">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-[#161814] divide-y divide-rs-jet-light">
                  {sorted.map((row) => {
                    const isPending = row._source === 'staging';
                    return (
                      <tr
                        key={`${row._source}-${row.id}`}
                        className={`transition-colors cursor-pointer ${
                          isPending
                            ? 'bg-rs-salmon/10 hover:bg-rs-salmon/20 border-l-4 border-l-rs-salmon'
                            : 'hover:bg-rs-jet'
                        }`}
                        onClick={() => handleEdit(row)}
                      >
                        <td className="px-4 py-3 text-rs-off-white font-mono text-xs align-top">
                          {formatDateDisplay(row.date)}
                          {isPending && (
                            <span className="block mt-1 text-xs bg-yellow-600/30 text-yellow-400 border border-yellow-600/40 rounded px-1.5 py-0.5 w-fit">
                              pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-rs-off-white/80 text-xs align-top">
                          {row.title || <span className="text-rs-off-white/30 italic">—</span>}
                        </td>
                        <td className="px-4 py-3 text-rs-off-white/70 align-top">
                          <p
                            className="line-clamp-2 text-xs leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: row.bullets }}
                          />
                        </td>
                        <td className="px-4 py-3 text-rs-off-white/40 text-xs align-top whitespace-nowrap">
                          {row.last_updated ? formatUpdated(row.last_updated) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right align-top" onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-3 justify-end">
                            <button
                              onClick={() => handleEdit(row)}
                              className="flex items-center gap-1 text-rs-green hover:text-rs-light-green transition-colors"
                              title="Edit record"
                            >
                              <Edit2 size={14} />
                              <span className="text-xs">Edit</span>
                            </button>
                            <button
                              onClick={() => { setDeleteId(row.id); setDeleteSource(row._source); }}
                              className="flex items-center gap-1 text-rs-red hover:text-rs-red/70 transition-colors"
                            >
                              <X size={14} />
                              <span className="text-xs">Delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <PendingPreviewModal
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}
        rows={staging}
      />

      <ConfirmationModal
        isOpen={deleteId != null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Bullet"
        message="Are you sure you want to delete this bullet? This cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
      />
    </>
  );
};

export default EmailBulletsPanel;
